import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ThemeToggle from '../../components/ThemeToggle';

const PanicButtonResult = () => {
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [archetype, setArchetype] = useState(null);
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  const [showTechniqueModal, setShowTechniqueModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Recuperar arquétipo escolhido
    const selectedArchetype = sessionStorage.getItem('panicArchetype');
    setArchetype(selectedArchetype);

    // Detectar dark mode e mobile
    const checkDarkMode = () => {
      const isDark = document.body.classList.contains('dark-mode');
      setIsDarkMode(isDark);
    };

    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkDarkMode();
    checkMobile();

    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    const handleResize = () => checkMobile();
    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Dados das técnicas organizadas por arquétipo
  const techniques = {
    leao: {
      title: 'Para crises intensas de ansiedade (estado de luta ou fuga)',
      subtitle: 'Técnicas do Leão',
      icon: '🦁',
      description: 'Você escolheu o Leão. Esse símbolo representa força e enfrentamento. Aqui você encontrará técnicas rápidas e poderosas para lidar com crises mais fortes de ansiedade.',
      methods: [
        {
          id: 'grounding',
          name: 'Técnica 5-4-3-2-1 (Grounding)',
          shortDesc: 'Usar os cinco sentidos para "aterrar" a mente no momento presente.',
          fullDesc: 'Técnica de mindfulness que interrompe ciclos de ansiedade ativando atenção plena no ambiente.',
          steps: [
            '5 coisas que você consegue ver',
            '4 coisas que você consegue tocar',
            '3 coisas que você consegue ouvir',
            '2 coisas que você consegue cheirar',
            '1 coisa que você consegue saborear'
          ],
          reference: 'Williams, J. M. G., Teasdale, J. D., Segal, Z. V., & Kabat-Zinn, J. (2007). The Mindful Way through Depression. Guilford Press.',
          color: '#e74c3c',
          video: '/video/Grounding_app.mp4'
        },
        {
          id: 'breathing',
          name: 'Respiração Profunda Diafragmática',
          shortDesc: 'Inspirar profundamente pelo nariz, expandindo o abdômen, e expirar lentamente pela boca.',
          fullDesc: 'Reduz hiperventilação, ativa o nervo vago e estimula o sistema nervoso parassimpático. Reduz frequência cardíaca e pressão arterial em estados de pânico.',
          steps: [
            'Coloque uma mão no peito e outra no abdômen',
            'Inspire lentamente pelo nariz por 4 segundos',
            'Sinta o abdômen se expandir (não o peito)',
            'Expire lentamente pela boca por 6 segundos',
            'Repita por 5-10 ciclos'
          ],
          reference: 'Ma, X., Yue, Z. Q., Gong, Z. Q., et al. (2017). The effect of diaphragmatic breathing on attention, negative affect and stress in healthy adults. Frontiers in Psychology, 8, 874.',
          color: '#c0392b',
          video: '/video/Diafragmatica_app.mp4'
        },
        {
          id: 'progressive',
          name: 'Relaxamento Muscular Progressivo',
          shortDesc: 'Contrair e relaxar cada grupo muscular por alguns segundos.',
          fullDesc: 'Técnica criada por Edmund Jacobson em 1920, muito usada em transtornos de ansiedade. Libera a tensão física acumulada na crise ansiosa.',
          steps: [
            'Deite-se ou sente-se confortavelmente',
            'Comece pelos pés: contraia por 5 segundos, relaxe',
            'Suba pelas pernas, abdômen, braços',
            'Termine no rosto e cabeça',
            'Sinta a diferença entre tensão e relaxamento'
          ],
          reference: 'Jacobson, E. (1938). Progressive Relaxation. University of Chicago Press.',
          color: '#8e44ad',
          video: '/video/Relax_Muscular_app.mp4'
        }
      ]
    },
    coala: {
      title: 'Para ansiedade leve/moderada e concentração',
      subtitle: 'Técnicas do Coala',
      icon: '🐨',
      description: 'Você escolheu o Coala. Esse símbolo representa calma e equilíbrio. Aqui você encontrará técnicas leves e contínuas para cultivar foco e reduzir a ansiedade no dia a dia.',
      methods: [
        {
          id: 'box-breathing',
          name: 'Respiração Quadrada (Box Breathing)',
          shortDesc: 'Inspirar por 4 segundos, segurar por 4, expirar por 4, segurar por 4.',
          fullDesc: 'Melhora foco e estabilidade emocional. Usada até por forças especiais (Navy SEALs). Regula o sistema nervoso autônomo e promove autocontrole.',
          steps: [
            'Inspire pelo nariz por 4 segundos',
            'Segure a respiração por 4 segundos',
            'Expire pela boca por 4 segundos',
            'Segure vazio por 4 segundos',
            'Repita o ciclo por 4-8 vezes'
          ],
          reference: 'Brown, R. P., & Gerbarg, P. L. (2005). Sudarshan Kriya yogic breathing in the treatment of stress, anxiety, and depression: Part II—clinical applications and guidelines. Journal of Alternative & Complementary Medicine, 11(4), 711–717.',
          color: '#27ae60',
          video: '/video/Respira_Quadrado_app.mp4'
        },
        {
          id: 'mindfulness',
          name: 'Mindfulness de 1 Minuto',
          shortDesc: 'Focar a atenção apenas na respiração ou em um som por 1 minuto.',
          fullDesc: 'Treina o cérebro para sair do "modo automático" e reduzir ruminação mental. Práticas curtas de mindfulness já mostram benefícios para ansiedade leve.',
          steps: [
            'Defina um timer para 1 minuto',
            'Sente-se confortavelmente',
            'Feche os olhos e foque na respiração',
            'Quando a mente divagar, volte gentilmente',
            'Termine respirando profundamente 3 vezes'
          ],
          reference: 'Kabat-Zinn, J. (1990). Full Catastrophe Living: Using the Wisdom of Your Body and Mind to Face Stress, Pain, and Illness. Delacorte.',
          color: '#2980b9',
          video: '/video/Mindfulness_app.mp4'
        },
        {
          id: 'visualization',
          name: 'Visualização Positiva (Safe Place)',
          shortDesc: 'Imaginar um lugar seguro e tranquilo, envolvendo todos os sentidos.',
          fullDesc: 'Estimula resposta de relaxamento e reduz a atividade da amígdala cerebral. Muito usada em terapia cognitivo-comportamental (TCC) e EMDR.',
          steps: [
            'Feche os olhos e respire profundamente',
            'Imagine um lugar onde se sente seguro',
            'Veja as cores, formas e detalhes',
            'Ouça os sons desse ambiente',
            'Sinta as sensações (temperatura, texturas)',
            'Permaneça por 2-5 minutos'
          ],
          reference: 'Shapiro, F. (2001). Eye Movement Desensitization and Reprocessing: Basic Principles, Protocols, and Procedures. Guilford Press.',
          color: '#f39c12',
          video: '/video/Safe_Place_app.mp4'
        }
      ]
    }
  };

  const currentTechniques = techniques[archetype] || techniques.leao;

  const openTechniqueModal = (technique) => {
    setSelectedTechnique(technique);
    setShowTechniqueModal(true);
  };

  const closeTechniqueModal = () => {
    setShowTechniqueModal(false);
    setSelectedTechnique(null);
  };

  // Estilos com background preto padronizado
  const containerStyles = {
    minHeight: '100vh',
    padding: '20px',
    background: '#000000',
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    color: '#ffffff'
  };

  const headerStyles = {
    textAlign: 'center',
    marginBottom: '2rem',
    maxWidth: '800px',
    margin: '0 auto 2rem'
  };

  const titleCardStyles = {
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(20px)',
    borderRadius: '15px',
    padding: isMobile ? '1.5rem' : '2rem',
    textAlign: 'center',
    maxWidth: '600px',
    margin: '0 auto 2rem',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
  };

  const descriptionStyles = {
    fontSize: isMobile ? '0.9rem' : '1rem',
    lineHeight: '1.6',
    opacity: 0.9,
    background: 'rgba(255, 255, 255, 0.1)',
    padding: '1rem',
    borderRadius: '10px',
    marginBottom: '2rem'
  };

  const cardsContainerStyles = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '1.5rem',
    maxWidth: '1200px',
    margin: '0 auto'
  };

  const cardStyles = (color) => ({
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(20px)',
    borderRadius: '15px',
    padding: '1.5rem',
    border: `2px solid ${color}20`,
    transition: 'all 0.3s ease',
    cursor: 'pointer'
  });

  const modalOverlayStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  };

  const modalContentStyles = {
    background: '#2c3e50',
    borderRadius: '15px',
    padding: '2rem',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '80vh',
    overflowY: 'auto',
    color: '#ffffff'
  };

  const videoStyles = {
    width: '100%',
    maxWidth: '300px',
    height: 'auto',
    borderRadius: '10px',
    marginBottom: '1.5rem',
    display: 'block',
    margin: '0 auto 1.5rem auto'
  };

  if (!archetype) {
    return (
      <div style={containerStyles}>
        <ThemeToggle />
        <div style={{textAlign: 'center', paddingTop: '2rem'}}>
          <h2>Nenhum arquétipo selecionado</h2>
          <button 
            onClick={() => navigate('/panic-button/step1')}
            style={{
              background: '#4ecdc4',
              color: '#ffffff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '25px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Voltar à seleção
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyles}>
      <ThemeToggle />
      
      <div style={headerStyles}>
        <div style={titleCardStyles}>
          <div style={{
            fontSize: isMobile ? '2.5rem' : '3rem',
            marginBottom: '1rem'
          }}>
            {currentTechniques.icon}
          </div>
          <h1 style={{
            fontSize: isMobile ? '1.5rem' : '2rem',
            marginBottom: '0.5rem',
            fontWeight: '700',
            color: '#ffffff',
            background: 'linear-gradient(135deg, #4ecdc4, #44a08d)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text'
          }}>
            {currentTechniques.title}
          </h1>
          <p style={{
            fontSize: isMobile ? '0.9rem' : '1rem',
            opacity: 0.8,
            marginBottom: 0,
            color: '#cccccc'
          }}>
            {currentTechniques.subtitle}
          </p>
        </div>
        
        <div style={descriptionStyles}>
          {currentTechniques.description}
        </div>
      </div>

      <div style={cardsContainerStyles}>
        {currentTechniques.methods.map((technique) => (
          <div 
            key={technique.id}
            style={cardStyles(technique.color)}
            onClick={() => openTechniqueModal(technique)}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-5px)';
              e.target.style.boxShadow = `0 10px 30px ${technique.color}30`;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <h3 style={{ 
              color: technique.color, 
              marginBottom: '1rem',
              fontSize: isMobile ? '1.1rem' : '1.3rem'
            }}>
              {technique.name}
            </h3>
            <p style={{ 
              fontSize: '0.9rem', 
              lineHeight: '1.5',
              opacity: 0.8,
              marginBottom: '1rem'
            }}>
              {technique.shortDesc}
            </p>
            <div style={{
              fontSize: '0.8rem',
              color: technique.color,
              fontWeight: '500'
            }}>
              🎥 Ver técnica completa →
            </div>
          </div>
        ))}
      </div>

      <div style={{
        textAlign: 'center',
        marginTop: '3rem'
      }}>
        <button
          onClick={() => navigate('/panic-button/step1')}
          style={{
            background: 'transparent',
            color: '#4ecdc4',
            border: '2px solid #4ecdc4',
            padding: '12px 24px',
            borderRadius: '25px',
            fontSize: '1rem',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          🔄 Escolher outro arquétipo
        </button>
      </div>

      {/* Modal de técnica */}
      {showTechniqueModal && selectedTechnique && (
        <div style={modalOverlayStyles} onClick={closeTechniqueModal}>
          <div style={modalContentStyles} onClick={(e) => e.stopPropagation()}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
              <h2 style={{color: selectedTechnique.color, margin: 0}}>
                {selectedTechnique.name}
              </h2>
              <button 
                onClick={closeTechniqueModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#ffffff'
                }}
              >
                ✕
              </button>
            </div>
            
            {selectedTechnique.video && (
              <video 
                style={videoStyles}
                controls 
                playsInline
                preload="none"
                poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5DbGlxdWUgcGFyYSByZXByb2R1emlyPC90ZXh0Pjwvc3ZnPg=="
                onLoadStart={() => console.log('Video loading started')}
                onCanPlay={() => console.log('Video can play')}
                onError={(e) => console.error('Video error:', e)}
              >
                <source src={selectedTechnique.video} type="video/mp4" />
                Seu navegador não suporta o elemento de vídeo.
              </video>
            )}
            
            <p style={{marginBottom: '1.5rem', lineHeight: '1.6'}}>
              <strong>Como fazer:</strong><br/>
              {selectedTechnique.fullDesc}
            </p>
            
            <div style={{marginBottom: '1.5rem'}}>
              <strong style={{color: selectedTechnique.color}}>📝 Passos:</strong>
              <ol style={{paddingLeft: '1.5rem', lineHeight: '1.8', marginTop: '0.5rem'}}>
                {selectedTechnique.steps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </div>
            
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '1rem',
              borderRadius: '8px',
              fontSize: '0.85rem',
              lineHeight: '1.4'
            }}>
              <strong>🔗 Referência científica:</strong><br/>
              {selectedTechnique.reference}
            </div>
            
            <button
              onClick={closeTechniqueModal}
              style={{
                background: selectedTechnique.color,
                color: '#ffffff',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '25px',
                fontSize: '1rem',
                cursor: 'pointer',
                marginTop: '1.5rem',
                width: '100%'
              }}
            >
              ✔️ Concluir
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PanicButtonResult;